import React, { useCallback, useMemo } from 'react'
import { Button, Grid, IconButton, TextField } from '@mui/material'
import { UseMutationResult } from '@tanstack/react-query'
import { InsertLink, Refresh, Share, Block, Undo } from '@mui/icons-material'

import {
  ContentSharedLinkIdRequest,
  ContentSharedLinkResponse,
  CreateContentSharedLinkRequest,
  GetContentSharedLinkResponse,
} from '@tabeeb/contracts/main-api/swagger-main-api'
import routes from '@tabeeb/routes'
import { onAddErrorNotification, onAddSuccessNotification } from '@tabeeb/modules/notification/actions'
import { useDispatch, useSelector } from '@tabeeb/store'
import { getCurrentUserTenantId } from '@tabeeb/modules/account/selectors'
import { getContentId } from '@tabeeb/shared/content/selectors'
import RegenerateSharedLinkDialog from '@tabeeb/modules/contentSharedLinks/components/RegenerateSharedLinkDialog'
import { useDialogState } from '@tabeeb/uikit'

export interface SharedContentLinkSettingsProps {
  sharedContentLinkResponse: GetContentSharedLinkResponse
  getSharedContentLinkLoading: boolean
  generateContentSharedLinkMutation: UseMutationResult<
    ContentSharedLinkResponse,
    unknown,
    CreateContentSharedLinkRequest
  >
  regenerateContentSharedLinkMutation: UseMutationResult<ContentSharedLinkResponse, unknown, ContentSharedLinkIdRequest>
  revokeContentSharedLinkMutation: UseMutationResult<void, unknown, ContentSharedLinkIdRequest>
  introduceContentSharedLinkMutation: UseMutationResult<void, unknown, ContentSharedLinkIdRequest>
  mutationLoading: boolean
}

const SharedContentLinkSettings: React.FC<SharedContentLinkSettingsProps> = ({
  sharedContentLinkResponse,
  getSharedContentLinkLoading,
  generateContentSharedLinkMutation,
  regenerateContentSharedLinkMutation,
  revokeContentSharedLinkMutation,
  introduceContentSharedLinkMutation,
  mutationLoading,
}) => {
  const dispatch = useDispatch()
  const currentTenantId = useSelector(getCurrentUserTenantId)
  const currentContentId = useSelector(getContentId)
  const contentSharedLinkAvailable = useMemo<boolean>(
    () => Boolean(sharedContentLinkResponse?.Token),
    [sharedContentLinkResponse?.Token]
  )

  const sharedLink = useMemo<string>(
    () =>
      contentSharedLinkAvailable
        ? `${window.location.origin}${routes.readonlyContent}/${sharedContentLinkResponse?.Token}`
        : '',
    [contentSharedLinkAvailable, sharedContentLinkResponse?.Token]
  )

  const handleGenerateLink = useCallback(() => {
    generateContentSharedLinkMutation.mutate({ ContentId: currentContentId, TenantId: currentTenantId })
  }, [generateContentSharedLinkMutation, currentContentId, currentTenantId])

  const handleRevokeLink = useCallback(() => {
    revokeContentSharedLinkMutation.mutate({
      ContentSharedLinkId: sharedContentLinkResponse?.Token as string,
    })
  }, [revokeContentSharedLinkMutation, sharedContentLinkResponse?.Token])

  const handleIntroduceLink = useCallback(() => {
    introduceContentSharedLinkMutation.mutate({
      ContentSharedLinkId: sharedContentLinkResponse?.Token as string,
    })
  }, [introduceContentSharedLinkMutation, sharedContentLinkResponse?.Token])

  const handleCopyLink = useCallback(async () => {
    await navigator.clipboard
      .writeText(sharedLink)
      .then(() => {
        dispatch(onAddSuccessNotification({ message: 'Copied to clipboard' }))
      })
      .catch(() => dispatch(onAddErrorNotification({ message: 'Failed to copy to clipboard' })))
  }, [dispatch, sharedLink])
  const [open, onOpen, onClose] = useDialogState()
  const onRegenerateLink = useCallback(() => {
    onClose()
    regenerateContentSharedLinkMutation.mutate({
      ContentSharedLinkId: sharedContentLinkResponse?.Token as string,
    })
  }, [onClose, regenerateContentSharedLinkMutation, sharedContentLinkResponse?.Token])

  return (
    <>
      <Grid container spacing={2} direction='row'>
        <Grid item xs={12} sx={{ display: 'flex' }}>
          <TextField
            label='Shared link'
            color='primary'
            variant='outlined'
            fullWidth
            InputProps={{ readOnly: true }}
            size='small'
            value={sharedLink}
          />
          {contentSharedLinkAvailable && (
            <IconButton
              title='Regenerate'
              color='primary'
              onClick={onOpen}
              disabled={mutationLoading || sharedContentLinkResponse?.IsDeleted}
            >
              <Refresh />
            </IconButton>
          )}
        </Grid>
        <Grid item xs={12}>
          {contentSharedLinkAvailable ? (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant='outlined'
                color='primary'
                disabled={getSharedContentLinkLoading || sharedContentLinkResponse?.IsDeleted}
                onClick={handleCopyLink}
                startIcon={<InsertLink />}
              >
                Copy link
              </Button>
              {sharedContentLinkResponse?.IsDeleted ? (
                <Button
                  variant='outlined'
                  color='primary'
                  disabled={getSharedContentLinkLoading}
                  onClick={() => handleIntroduceLink()}
                  startIcon={<Undo />}
                >
                  Introduce
                </Button>
              ) : (
                <Button
                  variant='outlined'
                  color='primary'
                  disabled={getSharedContentLinkLoading}
                  onClick={() => handleRevokeLink()}
                  startIcon={<Block />}
                >
                  Revoke
                </Button>
              )}
            </div>
          ) : (
            <Button
              variant='outlined'
              color='primary'
              disabled={getSharedContentLinkLoading}
              onClick={handleGenerateLink}
              startIcon={<Share />}
            >
              Generate shared link
            </Button>
          )}
        </Grid>
      </Grid>
      <RegenerateSharedLinkDialog open={open} onSubmit={onRegenerateLink} onClose={onClose} />
    </>
  )
}

export default SharedContentLinkSettings
